import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from './AxiosInstance';
import html2canvas from 'html2canvas';
import './styles/historyVerdict.css';
import Carte from './Carte'; // Import de la Carte

const HistoryVerdict = ({ gameState, refreshGameState }) => {
    const [verdict, setVerdict] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const verdictRef = useRef(null);
    const hasFetched = useRef(false); // Ref pour empêcher le double appel

    useEffect(() => {
        if (hasFetched.current) return; // Si déjà appelé, on arrête
        hasFetched.current = true;

        const fetchVerdict = async () => {
            try {
                const response = await axios.get('/verdict');
                setVerdict(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération du verdict", error);
                setLoading(false);
            }
        };
        fetchVerdict();
    }, []);

    const handleShare = async () => {
        if (!verdictRef.current) return;
        try {
            const canvas = await html2canvas(verdictRef.current, {
                scale: 2, // Meilleure résolution
                useCORS: true,
                backgroundColor: '#1a1a2e' // Assure un fond sombre
            });
            const image = canvas.toDataURL("image/png");

            if (navigator.share) {
                const blob = await (await fetch(image)).blob();
                const file = new File([blob], "geopolitikia_verdict.png", { type: "image/png" });
                await navigator.share({
                    title: 'Mon Jugement Historique - GeoPolitikIA',
                    text: verdict.share_catchphrase + " 🌍 #GeoPolitikIA www.giguelay.fr",
                    files: [file]
                });
            } else {
                const link = document.createElement('a');
                link.href = image;
                link.download = `geopolitikia_verdict_${Date.now()}.png`;
                link.click();
            }
        } catch (err) {
            console.error("Erreur partage", err);
        }
    };

    if (loading) return (
        <div className="loading-screen">
            <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <h2 className="text-2xl font-bold text-white">⚖️ Le Tribunal de l'Histoire délibère...</h2>
            </div>
        </div>
    );

    if (!verdict) return (
        <div className="error-screen">
            <p>Impossible de lire les archives historiques.</p>
            <button className="btn-secondary mt-4" onClick={() => navigate('/accueil')}>Retour</button>
        </div>
    );

    return (
        <div className="verdict-container">
            <div className="verdict-actions">
                <button className="btn-primary flex items-center gap-2" onClick={handleShare}>
                    <span>📸</span> Partager ma Légende
                </button>
                <button className="btn-secondary" onClick={() => navigate('/accueil')}>
                    Retour Accueil
                </button>
            </div>
            {/* Zone capturée pour le partage */}
            <div className="verdict-hero" ref={verdictRef}>
                <div className="verdict-header">
                    <div className="logo-container">
                        <img src="/logoGeo.svg" alt="GeoPolitikIA" className="game-logo" />
                    </div>
                </div>

                <h1 className="verdict-title">{verdict.ruler_title}</h1>

                {/* Affichage des années survécues */}
                <div className="verdict-years">
                    <span className="years-label">Règne :</span>
                    <span className="years-value">{verdict.years_survived} Ans</span>
                </div>

                <div className="verdict-scores">
                    <div className="score-item">
                        <span className="score-label">Éthique</span>
                        <span className="score-val" style={{ color: verdict.scores.ethics < 5 ? '#ef4444' : '#4ade80' }}>
                            {verdict.scores.ethics}/10
                        </span>
                    </div>
                    <div className="score-item">
                        <span className="score-label">Stratégie</span>
                        <span className="score-val" style={{ color: verdict.scores.strategy < 5 ? '#ef4444' : '#4ade80' }}>
                            {verdict.scores.strategy}/10
                        </span>
                    </div>
                    <div className="score-item">
                        <span className="score-label">Héritage</span>
                        <span className="score-val" style={{ color: verdict.scores.legacy < 5 ? '#ef4444' : '#4ade80' }}>
                            {verdict.scores.legacy}/10
                        </span>
                    </div>
                </div>

                {/* Prévisualisation de la carte si gameState est disponible */}
                {gameState && gameState.countries_dict && (
                    <div className="verdict-map-preview">
                        <Carte
                            countries_dict={gameState.countries_dict}
                            ocean={gameState?.climate?.ocean || 0}
                        // On désactive l'intéractivité si possible ou on laisse tel quel
                        />
                    </div>
                )}

                <div className="verdict-text">
                    <p className="text-white">"{verdict.verdict_text}"</p>
                </div>

                <div className="verdict-tags">
                    {verdict.archetypes.map(tag => <span key={tag} className="tag">{tag}</span>)}
                </div>

                <div className="verdict-footer">
                    <a href="https://www.giguelay.fr" className="game-url text-xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-violet-400 to-blue-400">www.giguelay.fr</a>
                </div>
            </div>
        </div>
    );
};

export default HistoryVerdict;
